import { useState, useEffect } from 'react';
import { X, Play, Pause, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { songsApi } from '../../api/songs';
import { useAudioStore } from '../../stores/audioStore';
import { useAuthStore } from '../../stores/authStore';
import { MarkdownRenderer } from './MarkdownRenderer';
import type { SongSummary, SongDetail as SongDetailType } from '../../types';

interface SongDetailProps {
  song: SongSummary;
  onClose: () => void;
}

export function SongDetail({ song, onClose }: SongDetailProps) {
  const [songDetail, setSongDetail] = useState<SongDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backgroundExpanded, setBackgroundExpanded] = useState(false);
  const [lyricsExpanded, setLyricsExpanded] = useState(true);

  const { currentSong, isPlaying, playSong, pause, resume } = useAudioStore();
  const { user } = useAuthStore();
  const isCurrentSong = currentSong?.id === song.id;

  useEffect(() => {
    loadSongDetail();
  }, [song.id]);

  const loadSongDetail = async () => {
    setLoading(true);
    setError(null);

    try {
      const detail = await songsApi.getSongById(song.id);
      setSongDetail(detail);
    } catch (err) {
      setError((err as Error).message || 'Failed to load song details');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (isCurrentSong) {
      if (isPlaying) {
        pause();
      } else {
        resume();
      }
    } else {
      playSong(song, true);
    }
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 bottom-0 w-full sm:w-[500px] bg-monster-dark z-50 overflow-y-auto shadow-2xl animate-slide-in">
        {/* Header */}
        <div className="sticky top-0 bg-monster-dark/95 backdrop-blur border-b border-monster-border z-10">
          <div className="flex items-center justify-between p-4">
            <h2 className="text-xl font-bold">Song Details</h2>
            <button
              onClick={onClose}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-card transition-colors"
            >
              <X size={32} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-monster-blue" />
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="card text-center">
              <p className="text-monster-crimson mb-2">Error loading details</p>
              <p className="text-sm text-gray-400">{error}</p>
            </div>
          </div>
        ) : songDetail ? (
          <div className="p-6 space-y-6">
            {/* Album Art */}
            <img
              src={songDetail.imagePath}
              alt={songDetail.title}
              className="w-full aspect-square object-cover rounded-lg shadow-lg"
            />

            {/* Title & Artist */}
            <div>
              <h1 className="text-3xl font-bold mb-2">{songDetail.title}</h1>
              <p className="text-xl text-gray-300 mb-1">{songDetail.artist}</p>
              {songDetail.genre && (
                <p className="text-sm text-gray-400">Genre: {songDetail.genre}</p>
              )}
              <p className="text-sm text-gray-400">
                Duration: {formatDuration(songDetail.durationSeconds)}
              </p>
              {user?.isAdmin && (
                <p className="text-sm text-gray-400 mt-2">
                  Song ID: <span className="font-mono text-monster-blue">{songDetail.id}</span>
                </p>
              )}
            </div>

            {/* Play Button */}
            <button onClick={handlePlayPause} className="w-full btn-primary py-3">
              {isCurrentSong && isPlaying ? (
                <>
                  <Pause size={20} className="inline mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play size={20} className="inline mr-2" />
                  Play
                </>
              )}
            </button>

            {/* Overview (formerly Teaser) */}
            {songDetail.teaser && (
              <div>
                <h3 className="text-lg font-semibold mb-2 text-monster-blue">Overview</h3>
                <MarkdownRenderer content={songDetail.teaser} />
              </div>
            )}

            {/* Sound Description (Optional) */}
            {songDetail.soundDescription && (
              <div>
                <h3 className="text-lg font-semibold mb-2 text-monster-blue">Sound</h3>
                <MarkdownRenderer content={songDetail.soundDescription} />
              </div>
            )}

            {/* Background (Collapsible) */}
            {songDetail.background && (
              <div className="border border-monster-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setBackgroundExpanded(!backgroundExpanded)}
                  className="w-full flex items-center justify-between p-4 hover:bg-monster-card transition-colors"
                >
                  <h3 className="text-lg font-semibold text-monster-blue">Background</h3>
                  {backgroundExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {backgroundExpanded && (
                  <div className="p-4 pt-0">
                    <MarkdownRenderer content={songDetail.background} />
                  </div>
                )}
              </div>
            )}

            {/* Lyrics (Collapsible, open by default) */}
            {songDetail.lyrics && (
              <div className="border border-monster-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setLyricsExpanded(!lyricsExpanded)}
                  className="w-full flex items-center justify-between p-4 hover:bg-monster-card transition-colors"
                >
                  <h3 className="text-lg font-semibold text-monster-blue">Lyrics</h3>
                  {lyricsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {lyricsExpanded && (
                  <div className="p-4 pt-0">
                    <MarkdownRenderer content={songDetail.lyrics} className="text-sm" />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
